/**
 * roro-pair-picker — «se combina con»: montar el set desde la ficha y meterlo
 * al carrito de una vez.
 *
 * QUÉ RESUELVE. La fila de círculos con «+» de la ficha vende EL SET, no el
 * complementario suelto. Al pulsar un círculo se abre un drop-up con la pieza
 * de la ficha y la elegida, cada una con sus selectores de variante y su
 * «− N +», un total y un solo botón que manda todas las líneas al carrito.
 *
 * SET LIBRE (2026-10-05, idea de Max). El set ya no es un par fijo:
 * «Додати ще виріб» abre un elegidor con pestañas por categoría y suma una
 * pieza más, las que se quieran. El quinto círculo, el punteado, abre el
 * mismo drop-up con solo la pieza de la ficha y el elegidor ya abierto. Los
 * productos del elegidor se piden al abrirlo a
 * `templates/collection.set-picker.liquid` (24 por página, scroll infinito),
 * así que la ficha no carga nada de esto hasta que alguien lo usa.
 *
 * POR QUÉ ES UN CUSTOM ELEMENT Y NO UN `init()` AL CARGAR. El panel de compra
 * se sustituye entero (`replaceWith`) cada vez que se toca una talla, así que
 * este nodo entra y sale del DOM varias veces por visita. `connectedCallback`
 * se vuelve a ejecutar solo; un `DOMContentLoaded` habría cableado el primer
 * nodo y dejado muertos todos los siguientes.
 *
 * DE DÓNDE SALE LA LÓGICA DE VARIANTES. `findVariant` / `valueHasStock` /
 * `reconcile` son las MISMAS tres reglas que ya usa el bottom sheet de
 * quick-add en `assets/roro-product-carousel.js`, sobre el mismo contrato JSON
 * (`snippets/roro-product-card-json.liquid`). Están reescritas aquí porque aquí
 * hay VARIOS productos vivos a la vez y allí uno; lo que no cambia es el
 * criterio: la cascada mira los grupos ANTERIORES, nunca los posteriores.
 *
 * CÓMO LLEGA AL CARRITO. Todas las líneas con la misma `properties._roro_pack`
 * y `_roro_pack_kind: 'pair'` (abierto desde un círculo con foto) o `'set'`
 * (desde el punteado). Es el contrato compartido con el tier 2 del panel y
 * `roro-gift-pack-builder`; quien lo dibuja agrupado es
 * `snippets/cart-products.liquid`. Ver `docs/theme-map.md`.
 */
(function () {
  'use strict';

  if (window.roroPairPickerReady) return;
  window.roroPairPickerReady = true;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /** El JSON puede venir vacío si el producto perdió sus complementarios. */
  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Respeta el `money_format` de la tienda. Copiado a propósito de
   * `blocks/roro-purchase-panel.liquid`: el fallback ingenuo escribía
   * «3 800 ₴» mientras Liquid pintaba «₴3,800.00» y el total del set cambiaba
   * de formato delante del cliente.
   */
  function formatMoney(cents, format) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, format || undefined);
    }

    var group = function (value, sep) {
      return value.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    };

    var out = (format || '').replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      var amount = (cents / 100).toFixed(2);
      var parts = amount.split('.');
      var whole = parts[0];
      var dec = parts[1];

      switch (name) {
        case 'amount_no_decimals':
          return group(Math.round(cents / 100).toString(), ',');
        case 'amount_with_comma_separator':
          return group(whole, '.') + ',' + dec;
        case 'amount_no_decimals_with_comma_separator':
          return group(Math.round(cents / 100).toString(), '.');
        case 'amount_with_space_separator':
          return group(whole, ' ') + ',' + dec;
        case 'amount_no_decimals_with_space_separator':
          return group(Math.round(cents / 100).toString(), ' ');
        default:
          return group(whole, ',') + '.' + dec;
      }
    });

    return out || (cents / 100).toFixed(2);
  }

  function packToken() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  /* ======================================================================
     Reglas de variante. Un `entry` es { product, selected } donde `selected`
     va indexado por la posición 0-based de la opción, igual que
     `variant.options`.
     ====================================================================== */

  function optionsOf(entry) {
    return (entry.product && entry.product.options) || [];
  }

  function firstAvailable(product) {
    var list = (product && product.variants) || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].available) return list[i];
    }
    return list[0] || null;
  }

  function findVariant(entry) {
    var opts = optionsOf(entry);
    var list = (entry.product && entry.product.variants) || [];

    // Sin opciones que elegir no hay nada que casar, pero tampoco hay que
    // devolver la primera a ciegas: la primera puede estar agotada.
    if (!opts.length) return firstAvailable(entry.product);

    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      var ok = true;
      for (var j = 0; j < opts.length; j++) {
        var idx = opts[j].index;
        if (entry.selected[idx] != null && v.options[idx] !== entry.selected[idx]) {
          ok = false;
          break;
        }
      }
      if (ok) return v;
    }
    return null;
  }

  /* Un valor está disponible si existe una variante con stock que lo lleve y
     que respete lo elegido en los grupos ANTERIORES. Los posteriores se
     ignoran: es la cascada de siempre — el color no se tacha porque una talla
     concreta esté agotada, la talla sí se tacha dentro de ese color. */
  function valueHasStock(entry, groupPos, idx, value) {
    var opts = optionsOf(entry);
    return ((entry.product && entry.product.variants) || []).some(function (v) {
      if (!v.available) return false;
      if (v.options[idx] !== value) return false;
      for (var j = 0; j < groupPos; j++) {
        var prev = opts[j].index;
        if (entry.selected[prev] != null && v.options[prev] !== entry.selected[prev]) return false;
      }
      return true;
    });
  }

  /* Al tocar una opción la combinación resultante puede no existir (esa talla
     no se hace en el color nuevo). En vez de dejar el set sin precio, se salta
     a la primera variante real que respeta lo que se acaba de tocar. */
  function reconcile(entry, groupPos) {
    if (findVariant(entry)) return;

    var opts = optionsOf(entry);
    var list = (entry.product && entry.product.variants) || [];
    var match = null;
    var fallback = null;

    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      var ok = true;
      for (var j = 0; j <= groupPos && j < opts.length; j++) {
        var idx = opts[j].index;
        if (entry.selected[idx] != null && v.options[idx] !== entry.selected[idx]) {
          ok = false;
          break;
        }
      }
      if (!ok) continue;
      if (!fallback) fallback = v;
      if (v.available) {
        match = v;
        break;
      }
    }

    var target = match || fallback;
    if (!target) return;
    opts.forEach(function (o) {
      entry.selected[o.index] = target.options[o.index];
    });
  }

  /** Siembra `selected` desde una variante concreta; si no llega, la primera con stock. */
  function seed(product, variantId) {
    var entry = { product: product, selected: [null, null, null] };
    var list = (product && product.variants) || [];
    var base = null;

    if (variantId != null) {
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].id) === String(variantId)) {
          base = list[i];
          break;
        }
      }
    }
    if (!base) base = firstAvailable(product);
    if (!base) return entry;

    optionsOf(entry).forEach(function (o) {
      entry.selected[o.index] = base.options[o.index];
    });
    return entry;
  }

  /* ====================================================================== */


  /** Tope de unidades por pieza: el mismo `QTY_MAX` del recuadro de la ficha. */
  var QTY_MAX = 10;

  /* El elegidor pide la página siguiente cuando el final de la lista queda a
     menos de esto del borde de abajo de la caja que hace scroll. */
  var PICKER_PREFETCH_PX = 320;

  /** Cuántas tarjetas grises se pintan mientras llega la primera página. */
  var PICKER_SKELETONS = 6;

  function make(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ====================================================================== */

  class RoroPairPicker extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;

      var data = readJSON(this.querySelector('[data-roro-pair-data]'));
      if (!data || !data.main) return;

      var partners = Array.isArray(data.partners) ? data.partners : [];
      var own =
        data.own && Array.isArray(data.own.collections) && data.own.collections.length ? data.own : null;
      if (!partners.length && !own) return;

      this.wired = true;
      this.data = data;
      this.own = own;
      this.strings = readJSON(this.querySelector('[data-roro-pair-strings]')) || {};
      this.moneyFormat = this.dataset.moneyFormat || '';
      this.uid = this.dataset.sheetId || 'RoroPairSheet';

      this.sheet = this.querySelector('[data-roro-pair-sheet]');
      this.panel = this.querySelector('[data-roro-pair-panel]');
      this.body = this.querySelector('[data-roro-pair-body]');
      this.totalEl = this.querySelector('[data-roro-pair-total]');
      this.totalRowEl = this.querySelector('[data-roro-pair-total-row]');
      this.totalLabelEl = this.querySelector('[data-roro-pair-total-label]');
      this.totalWasEl = this.querySelector('[data-roro-pair-total-was]');
      this.totalWasValueEl = this.querySelector('[data-roro-pair-total-was-value]');
      this.totalAfterEl = this.querySelector('[data-roro-pair-total-after]');

      // Umbral y porcentaje del codigo del set (`gift_pack_discount_*`). Los
      // manda Liquid SOLO si hay un codigo real configurado; ausentes = 0 y
      // `qualifies` da false, o sea una sola cifra como antes.
      this.discThreshold = parseInt(this.dataset.discThreshold, 10) || 0;
      this.discPct = parseFloat(this.dataset.discPct) || 0;
      // Piezas que pide el código (`gift_pack_discount_min_items`, 2).
      this.discMinItems = parseInt(this.dataset.discMinItems, 10);
      if (!(this.discMinItems >= 1)) this.discMinItems = 2;
      this.addBtn = this.querySelector('[data-roro-pair-add]');
      this.ctaTextEl = this.querySelector('[data-roro-pair-cta-text]');
      this.feedbackEl = this.querySelector('[data-roro-pair-feedback]');

      this.byId = Object.create(null);
      partners.forEach(function (p) {
        this.byId[String(p.id)] = p;
      }, this);

      this.icons = Object.create(null);
      this.querySelectorAll('template[data-roro-pair-icon]').forEach(function (tpl) {
        this.icons[tpl.dataset.roroPairIcon] = tpl;
      }, this);

      this.lines = [];
      this.picking = false;
      this.kind = 'pair';
      // Páginas ya pedidas, por URL de colección. Viven lo que vive este nodo:
      // cerrar y volver a abrir el drop-up no vuelve a pedir nada.
      this.pages = Object.create(null);
      this.tab = own ? Math.min(Math.max(parseInt(own.start, 10) || 0, 0), own.collections.length - 1) : 0;

      this.onKeydown = this.onKeydown.bind(this);
      this.onBodyScroll = this.onBodyScroll.bind(this);

      this.buildBody();

      this.querySelectorAll('[data-roro-pair-open]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          this.open(btn.dataset.roroPairOpen, btn);
        }.bind(this));
      }, this);

      var ownBtn = this.querySelector('[data-roro-pair-own]');
      ownBtn?.addEventListener('click', function () {
        this.openOwn(ownBtn);
      }.bind(this));

      this.querySelectorAll('[data-roro-pair-close], [data-roro-pair-overlay]').forEach(function (el) {
        el.addEventListener('click', this.close.bind(this));
      }, this);

      this.addBtn?.addEventListener('click', this.addSet.bind(this));
    }

    /* Si el panel se sustituye con el drop-up abierto, el scroll de la página se
       quedaría bloqueado para siempre. */
    disconnectedCallback() {
      if (this.isOpen) this.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      this.io?.disconnect();
    }

    /** Un SVG de los `<template>` del snippet, listo para meter en un botón. */
    icon(name) {
      var tpl = this.icons[name];
      return tpl ? tpl.content.cloneNode(true) : document.createTextNode('');
    }

    /**
     * Qué variante del producto principal se está viendo AHORA MISMO.
     *
     * No basta con el `mainVariantId` que escribió Liquid: al tocar una píldora
     * (Комплектація, Розмір…) el panel cambia de variante con
     * `replacePanel: false`, así que este nodo NO se vuelve a renderizar y su
     * JSON se queda con la variante de la carga inicial. Sin esto, quien elegía
     * «З блюдцем» y abría el set se llevaba al carrito la taza «Без блюдця»,
     * en silencio y por menos dinero del que la ficha estaba enseñando.
     *
     * Se lee del `dataset` y no de la propiedad JS porque el panel lo mantiene
     * al día ahí a propósito (`this.dataset.selectedVariantId = ...`), y así
     * esto sigue funcionando aunque el custom element aún no haya arrancado.
     */
    currentMainVariantId() {
      var panel = this.closest('roro-purchase-panel');
      var live = panel && (panel.dataset.selectedVariantId || panel.selectedVariantId);
      return live || this.data.mainVariantId;
    }

    /** Las unidades que ya marca el recuadro de la ficha: el set arranca con esas. */
    currentMainQuantity() {
      var panel = this.closest('roro-purchase-panel');
      var qty = panel ? parseInt(panel.quantity, 10) : 0;
      return qty > 1 ? Math.min(qty, QTY_MAX) : 1;
    }

    makeLine(entry, qty, role) {
      return { entry: entry, qty: qty || 1, role: role, fresh: false };
    }

    mainLine() {
      return this.makeLine(seed(this.data.main, this.currentMainVariantId()), this.currentMainQuantity(), 'main');
    }

    /* ---------------- Estructura fija del cuerpo ---------------- */

    /**
     * El cuerpo tiene tres piezas que se montan UNA vez: las líneas del set,
     * el botón «Додати ще виріб» y el elegidor. Al cambiar una unidad solo se
     * repintan precios y botones (`refresh`), y al sumar o quitar una pieza
     * solo las líneas (`renderLines`): si se rehiciera todo, la lista del
     * elegidor se volvería a pintar y el scroll saltaría arriba en cada toque.
     */
    buildBody() {
      if (!this.body) return;
      this.body.textContent = '';

      this.linesEl = make('div', 'roro-pair-sheet__lines');
      this.body.appendChild(this.linesEl);

      if (!this.own) return;

      this.moreBtn = make('button', 'roro-pair-sheet__more');
      this.moreBtn.type = 'button';
      var moreIcon = make('span', 'roro-pair-sheet__more-icon');
      moreIcon.setAttribute('aria-hidden', 'true');
      moreIcon.appendChild(this.icon('plus'));
      this.moreBtn.appendChild(moreIcon);
      this.moreBtn.appendChild(make('span', null, this.strings.addMore || ''));
      this.moreBtn.addEventListener('click', function () {
        this.setPicking(true);
        this.pickerTitleEl?.focus({ preventScroll: true });
        this.pickerEl?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }.bind(this));
      this.body.appendChild(this.moreBtn);

      this.pickerEl = this.buildPicker();
      this.body.appendChild(this.pickerEl);
      this.body.addEventListener('scroll', this.onBodyScroll, { passive: true });
    }

    buildPicker() {
      var section = make('section', 'roro-pair-sheet__picker');
      section.hidden = true;

      var head = make('div', 'roro-pair-sheet__picker-head');
      var top = make('div', 'roro-pair-sheet__picker-top');

      this.pickerTitleEl = make('h3', 'roro-pair-sheet__picker-title');
      this.pickerTitleEl.id = this.uid + '-PickerTitle';
      this.pickerTitleEl.tabIndex = -1;
      section.setAttribute('aria-labelledby', this.pickerTitleEl.id);
      top.appendChild(this.pickerTitleEl);

      this.pickerCloseBtn = make('button', 'roro-pair-sheet__picker-close');
      this.pickerCloseBtn.type = 'button';
      this.pickerCloseBtn.setAttribute('aria-label', this.strings.pickClose || '');
      this.pickerCloseBtn.appendChild(this.icon('close'));
      this.pickerCloseBtn.addEventListener('click', function () {
        this.setPicking(false);
        this.moreBtn?.focus({ preventScroll: true });
      }.bind(this));
      top.appendChild(this.pickerCloseBtn);
      head.appendChild(top);

      var tabs = make('div', 'roro-pair-sheet__tabs');
      tabs.setAttribute('role', 'tablist');
      tabs.setAttribute('aria-label', this.strings.categories || '');
      this.tabsEl = tabs;

      var gridId = this.uid + '-PickerGrid';
      this.tabBtns = this.own.collections.map(function (col, i) {
        var btn = make('button', 'roro-pair-sheet__tab', col.title);
        btn.type = 'button';
        btn.id = this.uid + '-Tab-' + i;
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-controls', gridId);
        btn.addEventListener('click', function () {
          this.selectTab(i);
        }.bind(this));
        btn.addEventListener('keydown', this.onTabKeydown.bind(this));
        tabs.appendChild(btn);
        return btn;
      }, this);
      head.appendChild(tabs);
      section.appendChild(head);

      this.gridEl = make('div', 'roro-pair-sheet__grid');
      this.gridEl.id = gridId;
      this.gridEl.setAttribute('role', 'tabpanel');
      section.appendChild(this.gridEl);

      this.pickerStatusEl = make('div', 'roro-pair-sheet__picker-status');
      this.pickerStatusEl.setAttribute('role', 'status');
      this.pickerStatusEl.setAttribute('aria-live', 'polite');
      section.appendChild(this.pickerStatusEl);

      this.sentinel = make('div', 'roro-pair-sheet__sentinel');
      this.sentinel.setAttribute('aria-hidden', 'true');
      section.appendChild(this.sentinel);

      // Dos disparadores para lo mismo, como en `roro-gift-pack-builder`: el
      // observer no corre cuando el documento no está pintando, y el `scroll`
      // pasivo del cuerpo hace de red.
      if ('IntersectionObserver' in window && this.body) {
        this.io = new IntersectionObserver(
          function (entries) {
            if (entries.some(function (e) { return e.isIntersecting; })) this.loadMore();
          }.bind(this),
          { root: this.body, rootMargin: '0px 0px ' + PICKER_PREFETCH_PX + 'px 0px' }
        );
        this.io.observe(this.sentinel);
      }

      return section;
    }

    /* ---------------- Abrir / cerrar ---------------- */

    /** Círculo con foto: la pieza de la ficha y la del círculo. */
    open(partnerId, trigger) {
      var partner = this.byId[String(partnerId)];
      if (!partner || !this.sheet) return;

      this.kind = 'pair';
      this.lines = [this.mainLine(), this.makeLine(seed(partner, null), 1, 'extra')];
      this.show(trigger, false);
    }

    /** Círculo punteado: la pieza de la ficha y el elegidor abierto. */
    openOwn(trigger) {
      if (!this.own || !this.sheet) return;

      this.kind = 'set';
      this.lines = [this.mainLine()];
      this.show(trigger, true);
    }

    show(trigger, picking) {
      this.lastFocus = trigger || null;
      this.setFeedback('');

      // La entrada la anima el propio CSS al dejar de estar `hidden`
      // (`animation` en `.roro-pair-sheet__panel`), sin clase de estado ni
      // `requestAnimationFrame`: el rAF no se ejecuta cuando el documento no
      // está pintando —la trampa que ya documenta `assets/roro-cart-pack.js`—
      // y el panel se habría quedado invisible en esa pestaña.
      this.sheet.hidden = false;
      if (this.body) this.body.scrollTop = 0;

      this.renderLines();
      this.setPicking(picking);

      this.lockScroll();
      this.isOpen = true;
      document.addEventListener('keydown', this.onKeydown);

      var first = this.panel && this.panel.querySelector('[data-roro-pair-close]');
      if (first) first.focus({ preventScroll: true });
    }

    close() {
      if (!this.sheet || this.sheet.hidden) return;

      this.isOpen = false;
      document.removeEventListener('keydown', this.onKeydown);
      this.unlockScroll();

      this.sheet.hidden = true;
      if (this.lastFocus && document.contains(this.lastFocus)) {
        this.lastFocus.focus({ preventScroll: true });
      }
      this.lastFocus = null;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.panel) return;

      // Focus trap básico: el diálogo es `aria-modal`, así que el tabulador no
      // puede salirse a la ficha de detrás.
      var items = Array.prototype.filter.call(
        this.panel.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null && el.tabIndex !== -1;
        }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    lockScroll() {
      var gap = window.innerWidth - document.documentElement.clientWidth;
      // En `<html>` y no en el body: con `overflow-x: clip` en el html (base.css),
      // `hidden` en el body lo convierte en contenedor de scroll y lo `sticky`
      // (la columna derecha de la ficha) salta a su sitio de arriba del todo.
      document.documentElement.style.overflow = 'hidden';
      if (gap > 0) document.body.style.paddingRight = gap + 'px';
    }

    unlockScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
    }

    /* ---------------- Líneas del set ---------------- */

    renderLines() {
      if (!this.linesEl) return;
      var top = this.body ? this.body.scrollTop : 0;

      this.linesEl.textContent = '';
      this.lines.forEach(function (line, i) {
        this.linesEl.appendChild(this.renderLine(line, i));
      }, this);

      if (this.body) this.body.scrollTop = top;
      this.refresh();
    }

    renderLine(line, index) {
      var entry = line.entry;
      var product = entry.product;
      var variant = findVariant(entry);

      var wrap = make('div', 'roro-pair-sheet__product');
      if (line.fresh) {
        // Destello corto en la pieza recién sumada desde el elegidor: sin él,
        // en un móvil la lista se cierra y no se ve dónde ha caído la pieza.
        wrap.classList.add('is-new');
        line.fresh = false;
      }

      // La foto lleva a la ficha, igual que el nombre: en Clarity (2026-09-27) se vio a una clienta
      // tocar la foto de este drop-up sin que pasara nada. Fuera del tabulador y oculta a lectores
      // porque el enlace del nombre ya es la misma parada.
      var media = make('a', 'roro-pair-sheet__media');
      media.href = product.url;
      media.tabIndex = -1;
      media.setAttribute('aria-hidden', 'true');

      var img = make('img', 'roro-pair-sheet__thumb');
      img.width = 72;
      img.height = 72;
      img.loading = 'lazy';
      img.alt = '';
      img.src = (variant && variant.image) || product.image || '';
      media.appendChild(img);
      wrap.appendChild(media);

      var info = make('div', 'roro-pair-sheet__info');

      var name = make('p', 'roro-pair-sheet__name');
      var link = make('a', null, product.title);
      link.href = product.url;
      name.appendChild(link);
      info.appendChild(name);

      // Precio y unidades en una fila: el precio a la izquierda, el «− N +» a la derecha. Si no
      // caben (precio tachado + rebajado en 320px), el selector baja solo a la línea siguiente.
      var buy = make('div', 'roro-pair-sheet__buy');

      // El precio también lleva a la ficha (2026-09-28): se tocaba igual que la foto. Fuera del
      // tabulador como la foto, pero sin `aria-hidden`: el precio sí se tiene que leer. Lo rellena
      // `paintLine`, que es quien sabe si el set ya llega al umbral.
      var price = make('a', 'roro-pair-sheet__price');
      price.href = product.url;
      price.tabIndex = -1;
      buy.appendChild(price);
      line.priceEl = price;

      buy.appendChild(this.renderQty(line));
      info.appendChild(buy);

      optionsOf(entry).forEach(function (opt, groupPos) {
        info.appendChild(this.renderOption(line, index, opt, groupPos));
      }, this);

      wrap.appendChild(info);
      return wrap;
    }

    /** El «− N +» de una pieza. Mismos tamaños y tope que el del recuadro de la ficha. */
    renderQty(line) {
      var group = make('div', 'roro-pair-sheet__qty');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', (this.strings.qtyLabel || '') + ': ' + line.entry.product.title);

      var minus = make('button', 'roro-pair-sheet__qty-btn');
      minus.type = 'button';
      minus.addEventListener('click', function () {
        this.stepLine(line, -1);
      }.bind(this));

      var value = make('output', 'roro-pair-sheet__qty-value');

      var plus = make('button', 'roro-pair-sheet__qty-btn');
      plus.type = 'button';
      plus.setAttribute('aria-label', this.strings.qtyIncrease || '');
      plus.appendChild(this.icon('plus'));
      plus.addEventListener('click', function () {
        this.stepLine(line, 1);
      }.bind(this));

      group.appendChild(minus);
      group.appendChild(value);
      group.appendChild(plus);

      line.minusBtn = minus;
      line.minusMode = '';
      line.valueEl = value;
      line.plusBtn = plus;
      return group;
    }

    renderOption(line, lineIndex, opt, groupPos) {
      var entry = line.entry;
      var group = make('div', 'roro-pair-sheet__option');

      var head = make('div', 'roro-pair-sheet__option-head');
      head.appendChild(make('span', null, opt.name));

      var divider = make('span', null, '|');
      divider.setAttribute('aria-hidden', 'true');
      head.appendChild(divider);

      head.appendChild(make('span', 'roro-pair-sheet__option-value', entry.selected[opt.index] || ''));
      group.appendChild(head);

      var list = make('div', 'roro-pair-sheet__values');
      list.setAttribute('role', 'radiogroup');
      list.setAttribute('aria-label', entry.product.title + ': ' + opt.name);

      opt.values.forEach(function (val) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', String(val.name === entry.selected[opt.index]));

        if (!valueHasStock(entry, groupPos, opt.index, val.name)) {
          btn.setAttribute('data-unavailable', 'true');
        }

        if (opt.visual && val.image) {
          btn.className = 'roro-pair-sheet__swatch';
          btn.setAttribute('aria-label', val.name);
          btn.title = val.name;

          var thumb = document.createElement('img');
          thumb.src = val.image;
          thumb.alt = '';
          thumb.width = 36;
          thumb.height = 36;
          thumb.loading = 'lazy';
          btn.appendChild(thumb);
        } else {
          btn.className = 'roro-pair-sheet__pill';
          btn.textContent = val.name;
        }

        btn.addEventListener('click', function () {
          entry.selected[opt.index] = val.name;
          reconcile(entry, groupPos);
          this.renderLines();
          this.refocus(lineIndex, groupPos);
        }.bind(this));

        list.appendChild(btn);
      }, this);

      group.appendChild(list);
      return group;
    }

    /* `renderLines()` reconstruye los botones, así que el foco no puede devolverse a
       un nodo guardado: se busca por línea y posición de grupo. */
    refocus(lineIndex, groupPos) {
      var card = this.linesEl && this.linesEl.children[lineIndex];
      if (!card) return;
      var groups = card.querySelectorAll('.roro-pair-sheet__option');
      var group = groups[groupPos];
      if (!group) return;
      var el = group.querySelector('[aria-checked="true"]');
      if (el) el.focus({ preventScroll: true });
    }

    /**
     * «−» con 1 unidad: la pieza de la ficha se queda (es la base del set) y
     * cualquier otra se quita, por eso en las demás ese botón es una papelera.
     */
    stepLine(line, delta) {
      if (delta < 0 && line.qty <= 1) {
        if (line.role !== 'main') this.removeLine(line);
        return;
      }
      line.qty = Math.min(QTY_MAX, Math.max(1, line.qty + delta));
      this.refresh();
    }

    removeLine(line) {
      var index = this.lines.indexOf(line);
      if (index < 1) return;
      this.lines.splice(index, 1);
      this.renderLines();
      if (this.picking) this.setPickerTitle();

      // El foco iba en la papelera, que ya no existe: a la pieza que ocupa su
      // sitio, o a «Додати ще виріб» si era la última.
      var next = this.lines[index] || null;
      if (next && next.minusBtn) {
        next.minusBtn.focus({ preventScroll: true });
      } else if (this.moreBtn && !this.moreBtn.hidden) {
        this.moreBtn.focus({ preventScroll: true });
      } else {
        this.lines[this.lines.length - 1]?.plusBtn?.focus({ preventScroll: true });
      }
    }

    /* ---------------- Precios y total ---------------- */

    /**
     * Lo que se paga si el set cruza el umbral del codigo del carrito, o null
     * si no lo cruza (o si no hay codigo configurado).
     *
     * El tema NO inventa la rebaja: el porcentaje sale de
     * `gift_pack_discount_pct` y quien pega `RORO-NABIR-5` al carrito es
     * `assets/roro-cart-bundle-discount.js`. Aqui solo se ANTICIPA la cifra
     * para que el cliente no tenga que llegar al carrito para saber cuanto va
     * a pagar. Desde el 2026-10-01 la rebaja va pieza a pieza (cada fila la
     * enseña) y el total es la suma, que es como la reparte Shopify.
     *
     * Desde el 2026-10-05 hacen falta además 2 unidades, la misma regla que el
     * recuadro de la ficha: una pieza sola de ₴3.000 va a precio normal (la
     * founder, 2026-10-02). Con el set libre eso ya puede pasar: el punteado
     * abre con una sola pieza. Desde el 2026-10-08 el carrito exige lo mismo
     * (`gift_pack_discount_min_items`), así que el «2» sale del ajuste.
     *
     * Es prudente por definicion: el carrito solo puede tener este set o más
     * (más piezas, más subtotal), asi que cuando aqui se promete la rebaja el
     * carrito la cumple seguro.
     */
    qualifies(total, units) {
      return units >= this.discMinItems && this.discThreshold > 0 && this.discPct > 0 && total >= this.discThreshold;
    }

    /** Una línea con el −X% restado, redondeada a la subunidad como Shopify. */
    discounted(cents) {
      return cents - Math.round((cents * this.discPct) / 100);
    }

    /** Repinta precios, unidades, total y CTA sin rehacer las líneas. */
    refresh() {
      var units = 0;
      var base = 0;
      var allResolved = this.lines.length > 0;
      var ready = this.lines.length > 0;

      var rows = this.lines.map(function (line) {
        var variant = findVariant(line.entry);
        var lineBase = variant ? (variant.priceCents || 0) * line.qty : 0;
        if (variant) {
          units += line.qty;
          base += lineBase;
        } else {
          allResolved = false;
        }
        if (!variant || !variant.available) ready = false;
        return { line: line, variant: variant, lineBase: lineBase };
      });

      var offer = this.qualifies(base, units);
      var pay = 0;

      rows.forEach(function (row) {
        var linePay = offer ? this.discounted(row.lineBase) : row.lineBase;
        pay += linePay;
        this.paintLine(row.line, row.variant, row.lineBase, offer ? linePay : null);
      }, this);

      if (this.totalEl) this.totalEl.textContent = allResolved ? formatMoney(pay, this.moneyFormat) : '';
      // Con una sola pieza (el punteado recién abierto) no hay set todavía:
      // «Разом», no «Разом за сет».
      if (this.totalLabelEl) {
        this.totalLabelEl.textContent =
          (this.lines.length > 1 ? this.strings.total : this.strings.totalSingle || this.strings.total) || '';
      }
      if (this.totalRowEl) this.totalRowEl.classList.toggle('is-discounted', offer);
      if (this.totalWasEl) {
        this.totalWasEl.hidden = !offer;
        if (this.totalWasValueEl) {
          this.totalWasValueEl.textContent = offer ? formatMoney(base, this.moneyFormat) : '';
        }
      }
      if (this.totalAfterEl) this.totalAfterEl.hidden = !offer;

      if (this.addBtn) this.addBtn.disabled = !ready;
      if (this.ctaTextEl) {
        var label = this.lines.length > 1 ? this.strings.add : this.strings.addSingle || this.strings.add;
        this.ctaTextEl.textContent = ready
          ? label || ''
          : (allResolved ? this.strings.soldOut : this.strings.selectOption) || '';
      }
    }

    /**
     * El precio de una línea es el de TODAS sus unidades, como el recuadro de
     * la ficha: con 2 tazas dice ₴3,500, no ₴1,750. Pasado el umbral, el de la
     * ficha tachado y el que se paga en rojo.
     */
    paintLine(line, variant, lineBase, linePay) {
      var price = line.priceEl;
      if (price) {
        price.textContent = '';
        if (!variant) {
          price.textContent = this.strings.soldOut || '';
        } else if (linePay !== null) {
          var was = make('s', 'roro-pair-sheet__price-was', formatMoney(lineBase, this.moneyFormat));
          var now = make('span', 'roro-pair-sheet__price-now', formatMoney(linePay, this.moneyFormat));
          price.appendChild(was);
          price.appendChild(document.createTextNode(' '));
          price.appendChild(now);
        } else {
          price.textContent = formatMoney(lineBase, this.moneyFormat);
        }
      }

      if (line.valueEl) line.valueEl.textContent = String(line.qty);
      if (line.plusBtn) line.plusBtn.disabled = line.qty >= QTY_MAX;

      var minus = line.minusBtn;
      if (!minus) return;

      var mode = line.role !== 'main' && line.qty <= 1 ? 'delete' : 'minus';
      if (mode !== line.minusMode) {
        line.minusMode = mode;
        minus.textContent = '';
        minus.appendChild(this.icon(mode));
        minus.classList.toggle('is-delete', mode === 'delete');
        minus.setAttribute(
          'aria-label',
          mode === 'delete'
            ? (this.strings.remove || '').replace('__TITLE__', line.entry.product.title)
            : this.strings.qtyDecrease || ''
        );
      }
      minus.disabled = line.role === 'main' && line.qty <= 1;
    }

    setFeedback(text, state) {
      if (!this.feedbackEl) return;
      this.feedbackEl.textContent = text || '';
      this.feedbackEl.hidden = !text;
      if (state) {
        this.feedbackEl.dataset.state = state;
      } else {
        delete this.feedbackEl.dataset.state;
      }
    }

    /* ---------------- Elegidor ---------------- */

    setPicking(on) {
      this.picking = Boolean(on) && Boolean(this.pickerEl);
      if (this.pickerEl) this.pickerEl.hidden = !this.picking;
      if (this.moreBtn) this.moreBtn.hidden = this.picking || !this.pickerEl;
      if (!this.picking) return;

      this.setPickerTitle();
      this.selectTab(this.tab);
    }

    /* Con solo la pieza de la ficha, el elegidor ES el drop-up: no se puede
       cerrar y pide «la segunda». Con dos o más, pide «otra» y se puede plegar. */
    setPickerTitle() {
      var first = this.lines.length < 2;
      if (this.pickerTitleEl) {
        this.pickerTitleEl.textContent = (first ? this.strings.pickFirst : this.strings.pickMore) || '';
      }
      if (this.pickerCloseBtn) this.pickerCloseBtn.hidden = first;
    }

    pageState() {
      var col = this.own.collections[this.tab];
      var key = col.url;
      if (!this.pages[key]) this.pages[key] = { items: [], next: 1, loading: false, error: false };
      return this.pages[key];
    }

    selectTab(index) {
      if (!this.own) return;
      this.tab = index;

      this.tabBtns.forEach(function (btn, i) {
        var on = i === index;
        btn.setAttribute('aria-selected', String(on));
        btn.tabIndex = on ? 0 : -1;
      });
      this.gridEl.setAttribute('aria-labelledby', this.tabBtns[index].id);
      this.revealTab(this.tabBtns[index]);

      this.renderGrid();
      var state = this.pageState();
      if (!state.items.length && state.next) this.loadMore();
    }

    /* Mueve el carril de pestañas en horizontal sin `scrollIntoView`, que
       también movería en vertical la caja del drop-up. */
    revealTab(btn) {
      var rail = this.tabsEl;
      if (!rail || !btn) return;
      var left = btn.offsetLeft - rail.offsetLeft;
      if (left < rail.scrollLeft) {
        rail.scrollLeft = left - 8;
      } else if (left + btn.offsetWidth > rail.scrollLeft + rail.clientWidth) {
        rail.scrollLeft = left + btn.offsetWidth - rail.clientWidth + 8;
      }
    }

    /* Flechas entre pestañas, el patrón de `tablist` de siempre. */
    onTabKeydown(event) {
      var count = this.tabBtns.length;
      var next = null;
      if (event.key === 'ArrowRight') next = (this.tab + 1) % count;
      if (event.key === 'ArrowLeft') next = (this.tab - 1 + count) % count;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = count - 1;
      if (next === null) return;
      event.preventDefault();
      this.selectTab(next);
      this.tabBtns[next].focus({ preventScroll: true });
    }

    renderGrid() {
      if (!this.gridEl) return;
      this.gridEl.textContent = '';
      var state = this.pageState();
      this.appendCards(state.items);
      if (state.loading && !state.items.length) this.renderSkeletons();
      this.renderPickerStatus();
    }

    renderSkeletons() {
      for (var i = 0; i < PICKER_SKELETONS; i++) {
        var ghost = make('div', 'roro-pair-sheet__card is-skeleton');
        ghost.setAttribute('aria-hidden', 'true');
        ghost.appendChild(make('span', 'roro-pair-sheet__card-media'));
        ghost.appendChild(make('span', 'roro-pair-sheet__card-name'));
        this.gridEl.appendChild(ghost);
      }
    }

    appendCards(list) {
      this.gridEl.querySelectorAll('.is-skeleton').forEach(function (el) {
        el.remove();
      });

      // La pieza de la ficha no sale en la lista: para llevarse más ya tiene su «− N +».
      var mainId = String(this.data.main.id);

      list.forEach(function (item) {
        var product = item.product;
        if (String(product.id) === mainId) return;
        var shown = firstAvailable(product);

        var card = make('button', 'roro-pair-sheet__card');
        card.type = 'button';

        var media = make('span', 'roro-pair-sheet__card-media');
        var img = make('img', 'roro-pair-sheet__card-img');
        img.src = item.thumb || product.image || '';
        img.alt = '';
        img.width = 120;
        img.height = 120;
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);

        var plus = make('span', 'roro-pair-sheet__card-plus');
        plus.setAttribute('aria-hidden', 'true');
        plus.appendChild(this.icon('plus'));
        media.appendChild(plus);
        card.appendChild(media);

        card.appendChild(make('span', 'roro-pair-sheet__card-name', product.title));
        card.appendChild(make('span', 'roro-pair-sheet__card-price', shown ? shown.price : ''));

        card.addEventListener('click', function () {
          this.pickProduct(product);
        }.bind(this));

        this.gridEl.appendChild(card);
      }, this);
    }

    renderPickerStatus() {
      var el = this.pickerStatusEl;
      if (!el) return;
      var state = this.pageState();
      var mainId = String(this.data.main.id);
      var visible = state.items.filter(function (item) {
        return String(item.product.id) !== mainId;
      }).length;
      el.textContent = '';

      if (state.error) {
        var retry = make('button', 'roro-pair-sheet__picker-retry', this.strings.loadError || '');
        retry.type = 'button';
        retry.addEventListener('click', this.loadMore.bind(this));
        el.appendChild(retry);
      } else if (state.loading && visible) {
        el.textContent = this.strings.loading || '';
      } else if (!state.loading && !visible && !state.next) {
        el.textContent = this.strings.empty || '';
      }
      el.hidden = !el.childNodes.length;
    }

    loadMore() {
      if (!this.picking || !this.own) return;

      var tab = this.tab;
      var col = this.own.collections[tab];
      var state = this.pageState();
      if (state.loading || !state.next) return;

      state.loading = true;
      state.error = false;
      if (!state.items.length) this.renderSkeletons();
      this.renderPickerStatus();

      var url = col.url + (col.url.indexOf('?') === -1 ? '?' : '&') + 'view=set-picker&page=' + state.next;

      fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (text) {
          // La plantilla devuelve JSON con `content-type: text/html` (no hay
          // forma de cambiarlo desde Liquid), así que se parsea a mano.
          var json = JSON.parse(text);
          var fresh = (Array.isArray(json.products) ? json.products : []).filter(function (item) {
            return item && item.product && item.product.id;
          });
          state.items = state.items.concat(fresh);
          state.next = json.next || null;
          state.loading = false;

          if (this.tab !== tab) return;
          this.appendCards(fresh);
          this.renderPickerStatus();
          // Si la página no llena la caja, el observer no vuelve a disparar
          // (el centinela nunca dejó de verse): se comprueba a mano.
          if (this.nearEnd()) this.loadMore();
        }.bind(this))
        .catch(function (error) {
          console.error('roro-pair-picker: no se pudo cargar el elegidor', error);
          state.loading = false;
          state.error = true;
          if (this.tab !== tab) return;
          this.gridEl.querySelectorAll('.is-skeleton').forEach(function (el) {
            el.remove();
          });
          this.renderPickerStatus();
        }.bind(this));
    }

    nearEnd() {
      if (!this.picking || !this.sentinel || !this.body) return false;
      var box = this.body.getBoundingClientRect();
      var end = this.sentinel.getBoundingClientRect();
      return end.top - box.bottom < PICKER_PREFETCH_PX;
    }

    onBodyScroll() {
      if (this.nearEnd()) this.loadMore();
    }

    /**
     * Una pieza elegida en la lista entra como línea nueva con su primera
     * variante con stock; sus opciones se eligen ya en la línea, como en las
     * piezas de los círculos. Si es una pieza sin opciones que ya está en el
     * set, suma una unidad a esa línea en vez de abrir otra idéntica.
     */
    pickProduct(product) {
      var target = null;

      if (!optionsOf({ product: product }).length) {
        target = this.lines.find(function (line) {
          return String(line.entry.product.id) === String(product.id);
        }) || null;
        if (target) target.qty = Math.min(QTY_MAX, target.qty + 1);
      }

      if (!target) {
        target = this.makeLine(seed(product, null), 1, 'extra');
        this.lines.push(target);
      }

      target.fresh = true;
      this.setPicking(false);
      this.renderLines();

      var row = this.linesEl.children[this.lines.indexOf(target)];
      if (row) {
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        var link = row.querySelector('.roro-pair-sheet__name a');
        if (link) link.focus({ preventScroll: true });
      }
    }

    /* ---------------- Carrito ---------------- */

    async addSet() {
      if (!this.addBtn || this.addBtn.disabled) return;

      var rows = this.lines.map(function (line) {
        return { variant: findVariant(line.entry), qty: line.qty };
      });
      if (!rows.length || rows.some(function (row) { return !row.variant || !row.variant.available; })) return;

      // La misma variante en dos líneas (la taza de la ficha elegida otra vez
      // en la lista) va al carrito como UNA línea con las unidades sumadas.
      var items = [];
      var byVariant = Object.create(null);
      rows.forEach(function (row) {
        var key = String(row.variant.id);
        if (byVariant[key]) {
          byVariant[key].quantity += row.qty;
        } else {
          byVariant[key] = { id: row.variant.id, quantity: row.qty };
          items.push(byVariant[key]);
        }
      });

      this.addBtn.disabled = true;
      var restore = this.ctaTextEl ? this.ctaTextEl.textContent : '';
      if (this.ctaTextEl) this.ctaTextEl.textContent = this.strings.adding || '';
      this.setFeedback('');

      try {
        // Token compartido por todas las líneas. `_roro_pack` es la propiedad
        // COMÚN a las superficies que crean packs, y es la que lee
        // `snippets/cart-products.liquid` para dibujarlas como un bloque con
        // cabecera en vez de sueltas. `_roro_pack_kind` dice cuál las creó, que
        // es lo que el Admin necesita para medir cada mecánica por separado.
        // El prefijo `_` las esconde del cliente en carrito y checkout, pero
        // siguen visibles en el Admin y en /cart.js, que es donde hacen falta.
        // Una sola línea no es un set: va sin propiedades, como desde la ficha.
        if (items.length > 1) {
          var props = { _roro_pack: packToken(), _roro_pack_kind: this.kind };
          items.forEach(function (item) {
            item.properties = props;
          });
        }

        // Las secciones del carrito se piden en el propio `add`, igual que hace
        // `product-form.js`, para que el cajón se re-renderice con el contenido
        // nuevo en vez de quedarse con el viejo.
        var sectionIds = Array.prototype.map
          .call(document.querySelectorAll('cart-items-component[data-section-id]'), function (el) {
            return el.dataset.sectionId;
          })
          .filter(Boolean);

        var payload = { items: items };
        if (sectionIds.length) payload.sections = sectionIds.join(',');

        // `RoroCart.post` lee el cuerpo como texto y solo lo parsea si es
        // JSON, así que un 429 de Shopify o una página de error no revientan
        // aquí con el mensaje interno del motor de JS («The string did not
        // match the expected pattern» en WebKit). Ver `assets/roro-cart-fetch.js`.
        var result = await window.RoroCart.post('/cart/add.js', payload);

        if (!result.ok) {
          this.setFeedback(window.RoroCart.message(result.data, null, this.strings.error), 'error');
          return;
        }

        // Del HTML del cajón que ya trae la respuesta; `/cart.js` solo si no
        // viene. Ver `RoroCart.cartAfterAdd`.
        var cart = await window.RoroCart.cartAfterAdd(result);

        // Se cierra ANTES de avisar: `cart:update` abre el cajón del carrito y
        // con el drop-up todavía puesto el cajón salía detrás del scrim.
        this.close();
        this.notifyCart(cart, result.data.sections, items[0].id);
      } catch (e) {
        console.error('roro-pair-picker: no se pudo añadir el set', e);
        this.setFeedback(this.strings.error || '', 'error');
      } finally {
        if (this.ctaTextEl) this.ctaTextEl.textContent = restore || this.strings.add || '';
        this.refresh();
      }
    }

    /**
     * Mismo contrato que `blocks/roro-purchase-panel.liquid` y que el quick-add
     * de `roro-product-carousel.js`: el evento es `cart:update` (sin la «d») y
     * `detail.data.itemCount` es lo que lee `<cart-icon>` para el contador.
     */
    notifyCart(cart, sections, variantId) {
      var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart || undefined,
            sourceId: 'roro-pair-picker',
            data: {
              source: 'roro-pair-picker',
              itemCount: count,
              variantId: String(variantId),
              didError: false,
              sections: sections
            }
          }
        })
      );

      // Red de seguridad para el contador, por si cambia el marcado del header.
      // Solo nodos hoja: nunca sobrescribir marcado ajeno.
      document
        .querySelectorAll('[data-cart-count], .cart-count, #cart-icon-bubble, .cart-bubble__text-count')
        .forEach(function (el) {
          if (el.children.length === 0) el.textContent = count < 100 ? String(count) : '99+';
          if (count > 0) el.classList.remove('hidden');
        });
    }
  }

  if (!customElements.get('roro-pair-picker')) {
    customElements.define('roro-pair-picker', RoroPairPicker);
  }
})();
